"use client";

import Link from "next/link";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";
import Header from "@/components/sections/header";
import Footer from "@/components/sections/footer";
import { TranslationsProvider } from "@/lib/i18n/TranslationsProvider";
import { BorderBeam } from "@/components/magicui/border-beam";
import { DotPattern } from "@/components/magicui/dot-pattern";
import { Home, Mail, ArrowRight } from "lucide-react";

export default function NotFound() {
  const pathname = usePathname();
  const isGerman = pathname.startsWith("/de");
  const locale = isGerman ? "de" : "en";

  const popularPages = [
    {
      href: `/${locale}`,
      text: isGerman ? "Startseite" : "Home",
      icon: <Home className="h-4 w-4" />,
    },
    {
      href: isGerman ? `/${locale}/kontakt` : `/${locale}/contact`,
      text: isGerman ? "Kontakt" : "Contact",
      icon: <Mail className="h-4 w-4" />,
    },
  ];

  return (
    <TranslationsProvider locale={locale}>
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 relative overflow-hidden">
          <DotPattern
            width={32}
            height={32}
            cx={1}
            cy={1}
            cr={1}
            className="absolute inset-0 h-full w-full dark:opacity-20 opacity-10"
          />
          <div className="container relative flex flex-1 flex-col items-center justify-center gap-8 py-32">
            <BorderBeam className="absolute inset-0 opacity-50" />
            <div className="relative space-y-6 text-center">
              <h1 className="text-[12rem] font-bold leading-none tracking-tighter">
                404
              </h1>
              <h2 className="text-3xl font-semibold">
                {isGerman ? "Seite nicht gefunden" : "Page Not Found"}
              </h2>
              <p className="text-muted-foreground max-w-[500px] mx-auto">
                {isGerman
                  ? "Die angeforderte Seite konnte nicht gefunden werden. Hier sind einige hilfreiche Links:"
                  : "The page you requested could not be found. Here are some helpful links:"}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 animate-in fade-in slide-in-from-bottom-4 duration-1000 fill-mode-both">
              {popularPages.map((page) => (
                <Link
                  key={page.href}
                  href={page.href}
                  className={cn(
                    buttonVariants({ variant: "default", size: "lg" }),
                    "group flex items-center gap-2 transition-all hover:scale-105"
                  )}
                >
                  {page.icon}
                  <span>{page.text}</span>
                  <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </TranslationsProvider>
  );
}
